import type { ReactNode, Ref } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { Badge, Card, EmptyPanel, ErrorPanel, LoadingPanel, Skeleton } from '../ui/primitives';
import { cx, type Tone } from '../ui/primitives/cx';
import type { Delta, PendingLoad, ReportContext, Segment } from '../../utils/reportsView';

// Rediseño · Bloque F — piezas que comparten las pantallas de Reportes. Solo dibujan: qué se dice y con
// qué cifra lo decide `utils/reportsView.ts`. Todas sus clases empiezan por `n3-reports`.

export const TITLE_ID = 'reports-title';

/**
 * El encabezado: el `h1` de la pantalla y su línea de apoyo; en un reporte, «‹ Reportes» encima (en P-030
 * va en la barra superior, que este shell no dibuja: B0, Bloque B). El `h1` recibe el foco cuando se
 * cambia de pantalla dentro de Reportes, para que el lector de pantalla diga dónde quedó.
 */
export function ReportsHeader({ title, lead, onBack, titleRef }: { title: string; lead: string; onBack?: () => void; titleRef: Ref<HTMLHeadingElement> }) {
  return (
    <header className="n3-reports__head">
      {onBack && (
        <button className="n3-reports__back" onClick={onBack} type="button">
          <Icon name="chevron-left" size={18} />
          <span>Reportes</span>
        </button>
      )}
      <h1 className="n3-reports__title" id={TITLE_ID} ref={titleRef} tabIndex={-1}>{title}</h1>
      <p className="n3-reports__lead">{lead}</p>
    </header>
  );
}

/** El mosaico pastel de una pregunta o de una cifra. Es adorno: el significado lo lleva el texto. */
export function ReportsTile({ icon, tone, size = 'md' }: { icon: IconName; tone: Tone | 'surface'; size?: 'md' | 'sm' }) {
  return (
    <span aria-hidden="true" className={cx('n3-reports__tile', `n3-reports__tile--${tone}`, size === 'sm' && 'n3-reports__tile--sm')}>
      <Icon name={icon} size={size === 'sm' ? 15 : 24} />
    </span>
  );
}

/**
 * Un importe con los centavos en chico (como el Inicio). El importe es el mismo, ya formateado: solo
 * cambia el tamaño de los centavos. Si no cabe, el renglón se parte ANTES de los centavos y, con una cifra
 * enorme y la letra ampliada, después de una coma de los miles: nunca a media cantidad.
 */
export function ReportsMoney({ label }: { label: string }) {
  const cut = label.lastIndexOf('.');
  if (cut === -1) return <>{label}</>;
  const groups = label.slice(0, cut).split(',');
  return (
    <span className="n3-reports__money">
      <span>{groups.map((group, index) => (index === 0 ? group : <span key={index}>,<wbr />{group}</span>))}</span>
      <wbr />
      <span className="n3-reports__money-cents">{label.slice(cut)}</span>
    </span>
  );
}

/**
 * Un texto con una cifra adentro («$1,000,000,000,137.15 más»): puede partirse después de cada coma de los
 * miles, nunca a media cantidad. El texto es el mismo.
 */
export function Breakable({ text }: { text: string }) {
  const parts = text.split(',');
  return <>{parts.map((part, index) => (index === 0 ? part : <span key={index}>,<wbr />{part}</span>))}</>;
}

/** Una frase con sus cifras en negritas. */
export function Sentence({ segments }: { segments: Segment[] }) {
  return <>{segments.map((segment, index) => (segment.strong ? <strong className={segment.amount ? 'n3-reports__amount' : undefined} key={index}>{segment.text}</strong> : <span key={index}>{segment.text}</span>))}</>;
}

/** La frase que resume el reporte, con la bombilla de P-030 (adorno). */
export function ReportsSay({ segments }: { segments: Segment[] }) {
  return (
    <p className="n3-reports__say">
      <span aria-hidden="true" className="n3-reports__say-icon"><Icon name="bulb" size={19} /></span>
      <span className="n3-reports__say-text"><Sentence segments={segments} /></span>
    </p>
  );
}

const ARROW: Record<Delta['direction'], string | null> = { up: '▲', down: '▼', flat: null };

/** Una variación: la flecha es adorno; «más» o «menos» lo dicen las palabras. */
export function DeltaText({ delta, className }: { delta: Delta; className: string }) {
  const arrow = ARROW[delta.direction];
  return (
    <p className={className}>
      {arrow && <span aria-hidden="true" className="n3-reports__arrow">{arrow}</span>}
      <span><Breakable text={delta.text} /></span>
    </p>
  );
}

/** El periodo que describen las cifras —el que devolvió el servidor— y el alcance, con lo que no incluye. */
export function ReportsContext({ context }: { context: ReportContext }) {
  return (
    <div className="n3-reports__context">
      <p className="n3-reports__context-row">
        <span className="n3-reports__context-period">
          <Icon name="calendar" size={16} />
          <span>{context.periodLabel}</span>
        </span>
        <Badge>{context.scopeLabel}</Badge>
      </p>
      {context.scopeNotes.length > 0 && (
        <ul className="n3-reports__scope-notes">
          {context.scopeNotes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * Lo que se ve mientras el reporte no tiene cifras: cargando (con la forma del reporte, sin ninguna
 * cifra), un fallo (con «Reintentar» si sirve de algo) o «sin acceso» con la razón del servidor. Ninguno
 * de los tres deja a la vista las cifras de antes.
 */
export function ReportsPending({ load, onRetry }: { load: PendingLoad; onRetry: () => void }) {
  if (load.status === 'loading') {
    return (
      <Card>
        <LoadingPanel label="Cargando tus números…">
          <Skeleton height="20px" width="70%" />
          <div className="n3-reports__pending-row">
            <Skeleton height="96px" />
            <Skeleton height="96px" />
          </div>
          <Skeleton height="110px" />
          <Skeleton width="45%" />
        </LoadingPanel>
      </Card>
    );
  }
  if (load.status === 'error') {
    return <ErrorPanel description={load.description} onRetry={load.canRetry ? onRetry : undefined} title={load.title} />;
  }
  return <EmptyPanel icon="info" title={load.message} />;
}

/** Una nota al pie del reporte, con su «i». */
export function ReportsNotes({ notes }: { notes: ReactNode[] }) {
  return (
    <ul className="n3-reports__notes">
      {notes.map((note, index) => (
        <li className="n3-reports__note" key={index}>
          <Icon name="info" size={14} />
          <span>{note}</span>
        </li>
      ))}
    </ul>
  );
}
